import parserHtml from 'prettier/plugins/html';
import parserMarkdown from 'prettier/plugins/markdown';
import prettier from 'prettier/standalone';
import xmlFormatter from 'xml-formatter';

export enum SupportedFormats {
  HTML = 'html',
  MARKDOWN = 'markdown',
  JSON = 'json',
  TEXT = 'text',
  XML = 'xml',
}

type ResponseObject = {
  text: string;
  format: SupportedFormats;
};

function detectFormat(transcript: string): SupportedFormats {
  const trimmedTranscript = transcript.trim();

  // Check for JSON
  if (trimmedTranscript.startsWith('{') || trimmedTranscript.startsWith('[')) {
    return SupportedFormats.JSON;
  }

  // Check for Markdown
  if (/^#|^```/.test(trimmedTranscript)) {
    return SupportedFormats.MARKDOWN;
  }

  // Check if it starts with "<" for XML or HTML
  if (trimmedTranscript.startsWith('<')) {
    // Use DOMParser to parse as XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(trimmedTranscript, 'application/xml');
    const htmlDoc = parser.parseFromString(trimmedTranscript, 'text/html');

    // Check for XML errors
    const isXml = !xmlDoc.querySelector('parsererror');
    if (isXml) {
      return SupportedFormats.XML;
    }

    // If no XML errors, check if it's HTML
    const isHtml = htmlDoc.body && htmlDoc.body.childNodes.length > 0;
    if (isHtml) {
      return SupportedFormats.HTML;
    }

    // If it's neither well-formed XML nor HTML, fallback to text
    return SupportedFormats.HTML;
  }

  // Fallback to text for other cases
  return SupportedFormats.TEXT;
}

/**
 * Beautifies an HTML transcript.
 * @param transcript - The raw HTML transcript.
 * @returns Pretty-formatted HTML.
 */
function prettyHtmlTranscriptAsync(transcript: string): Promise<string> {
  return prettier.format(transcript, {
    parser: 'html',
    plugins: [parserHtml],
  });
}

function prettyXmlTranscript(transcript: string): string {
  return xmlFormatter(transcript, {
    indentation: '  ', // Indent with 2 spaces
    collapseContent: true, // Collapse tags with no content into a single line
  });
}

/**
 * Beautifies a Markdown transcript.
 * @param transcript - The raw Markdown transcript.
 * @returns Pretty-formatted Markdown.
 */
function prettyMarkdownTranscriptAsync(transcript: string): Promise<string> {
  return prettier.format(transcript, {
    parser: 'markdown',
    plugins: [parserMarkdown],
  });
}

/**
 * Beautifies a JSON transcript.
 * @param transcript - The raw JSON transcript.
 * @returns Pretty-formatted JSON.
 */
function prettyJsonTranscript(transcript: string): string {
  try {
    const jsonObject = JSON.parse(transcript);
    return JSON.stringify(jsonObject, null, 2); // Pretty print with 2 spaces
  } catch {
    console.log('Invalid JSON format');
    return transcript;
  }
}

/**
 * Beautifies a plain text transcript.
 * @param transcript - The raw plain text transcript.
 * @returns A clean version of the plain text.
 */
function prettyPlainTextTranscript(transcript: string): string {
  return transcript
    .split('\n')
    .map((line) => line.trim())
    .join('\n');
}

/**
 * Formats and beautifies a transcript by detecting its language/format.
 * @param transcript - The raw transcript as a string.
 * @returns The beautified transcript.
 */
export async function prettyMessageAsStringAsync(transcript: string): Promise<ResponseObject> {
  const format = detectFormat(transcript);

  switch (format) {
    case SupportedFormats.HTML:
      return {
        text: await prettyHtmlTranscriptAsync(transcript),
        format: SupportedFormats.HTML,
      };
    case SupportedFormats.MARKDOWN:
      return {
        text: await prettyMarkdownTranscriptAsync(transcript),
        format: SupportedFormats.MARKDOWN,
      };
    case SupportedFormats.JSON:
      return {
        text: prettyJsonTranscript(transcript),
        format: SupportedFormats.JSON,
      };
    case SupportedFormats.TEXT:
      return {
        text: prettyPlainTextTranscript(transcript),
        format: SupportedFormats.TEXT,
      };
    case SupportedFormats.XML:
      return {
        text: prettyXmlTranscript(transcript),
        format: SupportedFormats.XML,
      };
    default:
      throw new Error('Unsupported transcript format');
  }
}

/**
 * Formats a single key-value pair for display.
 * Handles strings with newlines by indenting them.
 */
export function formatKeyValuePair(key: string, value: unknown): string {
  if (value === null || value === undefined) {
    return `${key}: (empty)`;
  }

  if (typeof value === 'string') {
    const cleanValue = value.replace(/\\n/g, '\n').replace(/\\"/g, '"');

    if (cleanValue.includes('\n')) {
      const lines = cleanValue.split('\n');
      const indentedLines = lines.map((line) => (line.trim() ? `  ${line}` : ''));
      return `${key}:\n${indentedLines.join('\n')}`;
    }

    return `${key}: ${cleanValue}`;
  }

  if (typeof value === 'object' && value !== null) {
    return `${key}: ${JSON.stringify(value)}`;
  }

  return `${key}: ${String(value)}`;
}

/**
 * Formats an object as key-value pairs, one per line.
 */
export function formatObjectAsKeyValuePairs(obj: Record<string, unknown>): string {
  return Object.entries(obj)
    .map(([key, value]) => formatKeyValuePair(key, value))
    .filter((pair) => pair !== '')
    .join('\n');
}

/**
 * Formats tool invocation params as readable key-value pairs.
 * Handles both JSON strings and objects with nested parsing.
 */
export function formatToolInvocationParams(data: unknown): string {
  if (data === null || data === undefined) {
    return '';
  }

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (typeof parsed === 'object' && parsed !== null) {
        return formatObjectAsKeyValuePairs(parsed);
      }
      return data;
    } catch {
      return data;
    }
  }

  if (typeof data === 'object' && data !== null) {
    return formatObjectAsKeyValuePairs(data as Record<string, unknown>);
  }

  return String(data);
}

/**
 * Formats tool invocation result as "key, value: value" pairs.
 * Matches the Activity step view format.
 * Object values are JSON stringified with indentation.
 */
export function formatToolInvocationResult(data: unknown): string {
  if (data === null || data === undefined) {
    return '';
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return String(value);
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (typeof parsed === 'object' && parsed !== null) {
        return Object.entries(parsed)
          .map(([key, value]) => `${key}, value: ${formatValue(value)}`)
          .join('\n');
      }
      return data;
    } catch {
      return data;
    }
  }

  if (typeof data === 'object' && data !== null) {
    return Object.entries(data as Record<string, unknown>)
      .map(([key, value]) => `${key}, value: ${formatValue(value)}`)
      .join('\n');
  }

  return String(data);
}
